import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation, useParams } from "react-router-dom";

import messages from "../../../../assets/locale/messages";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import { companyPermissionModules } from "../../../../utils/Constants";
import {
  canEditCompanyModule,
  canViewCompanyModules,
} from "../../../../utils/Permissions";
import {
  clearCompanyClient,
  getCompanyClientRequest,
} from "../../../../store/CompanyPortal/Clients/actions";

const useViewClient = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();

  const lang = useSelector((state) => state.locale.lang);
  const login = useSelector((state) => state.auth.login);
  const current = useSelector((state) => state.companyClients.current);
  const loading = useSelector((state) => state.companyClients.loading);
  const { clients } = messages[lang];

  const clientsModule = companyPermissionModules.manageClients;
  const canEdit = canEditCompanyModule(clientsModule, login);
  // ClientProfileEvents calls the Events API; only pass filterByClientId when
  // the user has Events view permission, otherwise the request returns 403.
  const canViewEvents = canViewCompanyModules(
    [companyPermissionModules.events],
    login,
  );

  const fallbackClient = location.state?.clientData;
  const client = current || fallbackClient || null;

  const goToClientsList = useCallback(() => {
    history.push(ROUTE_PATHS.companyClients);
  }, [history]);

  useEffect(() => {
    if (!id) return undefined;

    dispatch(
      getCompanyClientRequest({
        id,
        onError: () => goToClientsList(),
      }),
    );

    return () => dispatch(clearCompanyClient());
  }, [dispatch, id, goToClientsList]);

  const breadcrumbLabel =
    clients.listClients.listClientsBreadcrumb || clients.title;

  const breadcrumbList = useMemo(
    () => [
      { path: ROUTE_PATHS.companyClients, label: breadcrumbLabel },
      {
        path: "",
        label: client?.name || "",
      },
    ],
    [breadcrumbLabel, client?.name],
  );

  const handleEditProfile = useCallback(() => {
    if (!id || !client) return;

    history.push({
      pathname: ROUTE_PATHS.companyEditClient.replace(":id", id),
      state: { clientData: client },
    });
  }, [history, id, client]);

  return {
    clients,
    breadcrumbList,
    client,
    loading,
    canEdit,
    filterByClientId: canViewEvents ? (client?.client_id ?? null) : null,
    onEditProfile: handleEditProfile,
  };
};

export default useViewClient;
